// Phone number utility functions using libphonenumber-js
import { parsePhoneNumberFromString, getCountryCallingCode, AsYouType } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';

/**
 * Converts a phone number with country prefix (e.g., +44) to country code (e.g., GB)
 * @param phoneNumber - Phone number with country prefix like "+447493068710"
 * @returns Object with country_code and number
 */
export const parsePhoneNumber = (phoneNumber: string): { country_code: string; number: string } => {
  try {
    const parsed = parsePhoneNumberFromString(phoneNumber);
    
    if (parsed && parsed.isValid()) {
      return {
        country_code: parsed.country || 'GB',
        number: parsed.nationalNumber
      };
    }
  } catch (error) {
    console.warn('Error parsing phone number:', error);
  }
  
  // Fallback to default
  return {
    country_code: 'GB',
    number: phoneNumber.replace(/[^\d]/g, '')
  };
};

/**
 * Converts country code to phone prefix
 * @param countryCode - Country code like "GB"
 * @returns Phone prefix like "+44"
 */
export const getPhonePrefix = (countryCode: string): string => {
  try {
    const callingCode = getCountryCallingCode(countryCode as CountryCode);
    return `+${callingCode}`;
  } catch (error) {
    console.warn('Error getting phone prefix for country:', countryCode, error);
    return '+44'; // Default fallback
  }
};

/**
 * Formats phone number for display
 * @param countryCode - Country code like "GB"
 * @param number - Phone number like "07493068710"
 * @returns Formatted phone number like "+44 07493068710"
 */
export const formatPhoneNumber = (countryCode: string, number: string): string => {
  try {
    const prefix = getPhonePrefix(countryCode);
    return `${prefix} ${number}`;
  } catch (error) {
    console.warn('Error formatting phone number:', error);
    return `+44 ${number}`;
  }
};

/**
 * Validates if a phone number is valid for a given country
 * @param phoneNumber - Phone number to validate
 * @param countryCode - Country code to validate against
 * @returns True if valid, false otherwise
 */
export const isValidPhoneNumber = (phoneNumber: string, countryCode?: string): boolean => {
  try {
    const parsed = parsePhoneNumberFromString(phoneNumber, countryCode as CountryCode);
    return parsed ? parsed.isValid() : false;
  } catch {
    return false;
  }
};

/**
 * Formats phone number as user types (useful for input fields)
 * @param phoneNumber - Phone number being typed
 * @param countryCode - Country code (optional)
 * @returns Formatted phone number
 */
export const formatAsYouType = (phoneNumber: string, countryCode?: string): string => {
  try {
    const formatter = new AsYouType(countryCode as CountryCode);
    return formatter.input(phoneNumber);
  } catch (error) {
    console.warn('Error formatting phone number as you type:', error);
    return phoneNumber;
  }
};

/**
 * Gets all supported countries with their calling codes
 * @returns Array of country objects with code and calling code
 */
export const getSupportedCountries = (): Array<{ code: string; callingCode: string; name: string }> => {
  // This is a simplified version - in practice, you might want to use a more comprehensive list
  const commonCountries = [
    { code: 'GB', callingCode: '+44', name: 'United Kingdom' },
    { code: 'US', callingCode: '+1', name: 'United States' },
    { code: 'IN', callingCode: '+91', name: 'India' },
    { code: 'AU', callingCode: '+61', name: 'Australia' },
    { code: 'CA', callingCode: '+1', name: 'Canada' },
    { code: 'DE', callingCode: '+49', name: 'Germany' },
    { code: 'FR', callingCode: '+33', name: 'France' },
    { code: 'IT', callingCode: '+39', name: 'Italy' },
    { code: 'ES', callingCode: '+34', name: 'Spain' },
    { code: 'JP', callingCode: '+81', name: 'Japan' },
    { code: 'CN', callingCode: '+86', name: 'China' },
    { code: 'BR', callingCode: '+55', name: 'Brazil' },
    { code: 'RU', callingCode: '+7', name: 'Russia' },
    { code: 'ZA', callingCode: '+27', name: 'South Africa' },
    { code: 'MX', callingCode: '+52', name: 'Mexico' },
    { code: 'AR', callingCode: '+54', name: 'Argentina' },
    { code: 'CL', callingCode: '+56', name: 'Chile' },
    { code: 'CO', callingCode: '+57', name: 'Colombia' },
    { code: 'PE', callingCode: '+51', name: 'Peru' },
    { code: 'VE', callingCode: '+58', name: 'Venezuela' },
    { code: 'EG', callingCode: '+20', name: 'Egypt' },
    { code: 'NG', callingCode: '+234', name: 'Nigeria' },
    { code: 'KE', callingCode: '+254', name: 'Kenya' },
    { code: 'GH', callingCode: '+233', name: 'Ghana' },
    { code: 'MA', callingCode: '+212', name: 'Morocco' },
    { code: 'TN', callingCode: '+216', name: 'Tunisia' },
    { code: 'DZ', callingCode: '+213', name: 'Algeria' },
    { code: 'TH', callingCode: '+66', name: 'Thailand' },
    { code: 'VN', callingCode: '+84', name: 'Vietnam' },
    { code: 'ID', callingCode: '+62', name: 'Indonesia' },
    { code: 'MY', callingCode: '+60', name: 'Malaysia' },
    { code: 'SG', callingCode: '+65', name: 'Singapore' },
    { code: 'PH', callingCode: '+63', name: 'Philippines' },
    { code: 'KR', callingCode: '+82', name: 'South Korea' },
    { code: 'TW', callingCode: '+886', name: 'Taiwan' },
    { code: 'HK', callingCode: '+852', name: 'Hong Kong' },
    { code: 'MO', callingCode: '+853', name: 'Macau' },
    { code: 'NZ', callingCode: '+64', name: 'New Zealand' }
  ];
  
  return commonCountries;
};
